import { IssuedTicket } from './IssuedTicket';

// Domain
export type TicketType = TicketTypeBE;

export interface HistoryBooking {
  bookingId: number;
  invoiceIds: number[];
  tickets: HistoryTicket[];
}

export interface HistoryTicket {
  ticketId: number;
  agencyId: string;
  ticketType: TicketType;
  passName: string;
  hasTicket: boolean;
  hasReservation: boolean;
  isReservable: boolean;
  drives: HistoryDrive[];
}

export interface HistoryDrive {
  driveId: number;
  segment: number;
  fromStationId: string;
  fromStationName: string;
  fromStationDeparture: Date;
  toStationId: string;
  toStationName: string;
  toStationArrival: Date;
  seatNo: string;
  hasSeat: boolean;
  lastStatus: string;
  resExpiry: Date;
  carrierLogo: string;
  isReturn: boolean;
}

// BE
enum TicketTypeBE {
  ONEWAY,
  TWOWAY,
  MONTHLY,
  SEGMENTED,
}

export interface HistoryBookingBE {
  bookingId: number;
  invoiceIds: number[];
  ticketDtos: HistoryTicketBE[];
}

interface HistoryTicketBE {
  ticketId: number;
  agencyId: string;
  ticketType: TicketTypeBE;
  passName: string;
  hasTicket: boolean;
  hasReservation: boolean;
  reservable: boolean;
  driveDtos: HistoryDriveBE[];
}

interface HistoryDriveBE {
  driveId: number;
  segment: number;
  fromStationId: string;
  fromStationName: string;
  fromStationDeparture: string;
  toStationId: string;
  toStationName: string;
  toStationArrival: string;
  seatNo: string;
  hasSeat: boolean;
  lastStatus: string;
  resExpiry: string;
  carrierLogo: string;
  return: boolean;
}

// BE to Domain
export const createHistoryBookingFromBE = ({ ticketDtos, ...rest }: HistoryBookingBE): HistoryBooking => ({
  ...rest,
  tickets: ticketDtos.map(createHistoryTicketFromBE),
});

const createHistoryTicketFromBE = ({ driveDtos, reservable, ...rest }: HistoryTicketBE): HistoryTicket => ({
  ...rest,
  isReservable: reservable,
  drives: driveDtos.map(createHistoryDriveFromBE),
});

const createHistoryDriveFromBE = (
  { fromStationDeparture, toStationArrival, resExpiry, return: isReturn, ...rest }: HistoryDriveBE,
): HistoryDrive => ({
  ...rest,
  fromStationDeparture: fromStationDeparture ? new Date(fromStationDeparture) : undefined,
  toStationArrival: toStationArrival ? new Date(toStationArrival) : undefined,
  resExpiry: resExpiry ? new Date(resExpiry) : undefined,
  isReturn,
});

export const createIssuedTicketsFromHistoryBooking = ({ tickets, bookingId }: HistoryBooking): IssuedTicket[] =>
  tickets.reduce((acc, ticket) => acc.concat(createsIssuedTicketsFromHistoryTicketAndBookingId(ticket, bookingId)), []);

export const createsIssuedTicketsFromHistoryTicketAndBookingId = (ticket: HistoryTicket, bookingId: number, onlyReturn = false) => {
  return ticket.drives
    .filter(drive => onlyReturn ? drive.isReturn : true)
    .map(drive => createIssuedTicketFromHistoryDriveAndHistoryTicketAndBookingId(drive, ticket, bookingId));
};

export const createIssuedTicketFromHistoryDriveAndHistoryTicketAndBookingId = (
  {
    driveId, segment, fromStationId, fromStationName, resExpiry, toStationArrival, fromStationDeparture, carrierLogo, hasSeat,
    lastStatus, seatNo, toStationId, toStationName, isReturn,
  }: HistoryDrive,
  { drives, ticketId, agencyId, passName, hasReservation, hasTicket, ticketType, isReservable }: HistoryTicket,
  bookingId: number,
) => {
  const [firstName, lastName] = passName.split(' ');

  const issuedTicket = new IssuedTicket();
  issuedTicket.driveId = driveId;
  issuedTicket.ticketId = ticketId;
  issuedTicket.agencyTicketId = agencyId;
  issuedTicket.bookingId = bookingId;
  issuedTicket.ticketType = ticketType.toString();
  issuedTicket.firstName = firstName;
  issuedTicket.lastName = lastName;
  issuedTicket.fromStationId = fromStationId;
  issuedTicket.fromStationName = fromStationName;
  issuedTicket.fromStationDeparture = fromStationDeparture;
  issuedTicket.toStationId = toStationId;
  issuedTicket.toStationName = toStationName;
  issuedTicket.toStationArrival = toStationArrival;
  issuedTicket.segment = segment;
  issuedTicket.isReturn = isReturn;
  issuedTicket.hasSeat = hasSeat;
  issuedTicket.hasReservation = hasReservation;
  issuedTicket.hasTicket = hasTicket;
  issuedTicket.expiryDate = resExpiry;
  issuedTicket.lastStatus = lastStatus;
  issuedTicket.confirmed = false;
  issuedTicket.carrierLogoUUID = carrierLogo;
  issuedTicket.seatNumber = seatNo;
  issuedTicket.isReservable = isReservable;
  return issuedTicket;
};



// WEBPACK FOOTER //
// ./src/app/shared/model/History.ts